import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { crypto as cryptoShared } from '@photo-vault/shared';
import type { dto } from '@photo-vault/shared';
import { fromBase64Url } from '@salvobee/crypto-vault';
import { AntiAutofillHoneypot, Button, Card, Icon, Input, PageHeader } from '../../ui/index.js';
import { useAuth, useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { stashRecoveryCode } from './recoveryStorage.js';

// Recovery-code management for a user who is already unlocked. No new endpoint
// is needed: rsaPriv is in memory and the passphrase is known, so everything is
// a client-side rebuild plus the existing change-passphrase write.

type Mode = 'rotate' | 'check';
type Busy = 'none' | 'working';

export function RegenerateRecoveryCodePage() {
  const navigate = useNavigate();
  const session = useAuthSession();
  const { setSession } = useAuth();
  const { metadata } = useInfra();

  const [mode, setMode] = useState<Mode>('rotate');
  const [passphrase, setPassphrase] = useState('');
  const [candidateCode, setCandidateCode] = useState('');
  const [busy, setBusy] = useState<Busy>('none');
  const [error, setError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<'valid' | 'invalid' | null>(null);

  async function handleRotate(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy('working');
    try {
      // changePassphrase always rewrites salt/wrappedPriv/verifier, so we cannot
      // rotate the recovery material alone. Re-deriving from the *same*
      // passphrase against a fresh salt is a valid no-op change — and rotating
      // the salt is a small bonus.
      const { verifierB64: currentVerifierB64 } = await cryptoShared.deriveMasterFromStoredSalt(
        passphrase,
        session.saltB64,
      );
      const rebuilt = await cryptoShared.rebuildKeyMaterialForNewPassphrase(
        session.rsaPriv,
        passphrase,
      );
      const recovery = await cryptoShared.rebuildRecoveryMaterial(session.rsaPriv);

      await metadata.changePassphrase(
        {
          saltB64: rebuilt.saltB64,
          wrappedPriv: rebuilt.wrappedPriv,
          verifierB64: rebuilt.verifierB64,
          saltRecoveryB64: recovery.saltRecoveryB64,
          wrappedPrivRecovery: recovery.wrappedPrivRecovery,
          verifierRecoveryB64: recovery.verifierRecoveryB64,
          proof: { kind: 'passphrase', currentVerifierB64 },
        } satisfies dto.ChangePassphraseRequest,
        session.jwt,
      );

      setSession({ ...session, masterKey: rebuilt.masterKey, saltB64: rebuilt.saltB64 });
      stashRecoveryCode(recovery.recoveryCode, 'rotated');
      navigate('/recovery-code', { replace: true });
    } catch (err) {
      setBusy('none');
      setError(err instanceof Error ? err.message : 'Rigenerazione fallita. Riprova.');
    }
  }

  // Purely local: fetch the wrapped material and try to unwrap it. Nothing is
  // written, so a wrong guess costs nothing.
  async function handleCheck(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setCheckResult(null);
    setBusy('working');
    try {
      const bootstrap = await metadata.recoveryBootstrap(session.jwt);
      const { masterKey } = await cryptoShared.deriveMaster(
        cryptoShared.normaliseRecoveryCode(candidateCode),
        fromBase64Url(bootstrap.saltRecoveryB64),
      );
      await cryptoShared.unwrapPrivateKey(bootstrap.wrappedPrivRecovery, masterKey);
      setCheckResult('valid');
    } catch {
      setCheckResult('invalid');
    } finally {
      setBusy('none');
    }
  }

  const working = busy === 'working';

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-7">
      <PageHeader
        eyebrow="codice di recupero"
        title={
          <>
            Gestisci il tuo <span className="accent-text">codice</span>
          </>
        }
        description="Il codice di recupero è l'unica via di rientro se dimentichi la passphrase. Decifra la tua chiave RSA privata senza passare dal server."
      />

      <div className="rounded-xl border border-outline bg-surface-container-low px-4 py-3 text-sm text-on-surface-variant">
        <p>
          <strong className="text-on-surface">Non possiamo rimostrarti il codice attuale.</strong>{' '}
          Il server conserva solo materiale derivato da esso, mai il codice in chiaro. Se non lo
          trovi più, l&apos;unica opzione è generarne uno nuovo — il precedente smetterà di
          funzionare.
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          variant={mode === 'rotate' ? 'primary' : 'ghost'}
          onClick={() => setMode('rotate')}
          disabled={working}
        >
          Genera un nuovo codice
        </Button>
        <Button
          variant={mode === 'check' ? 'primary' : 'ghost'}
          onClick={() => setMode('check')}
          disabled={working}
        >
          Verifica quello che ho
        </Button>
      </div>

      <Card className="flex flex-col gap-6">
        {mode === 'rotate' ? (
          <form className="flex flex-col gap-3" onSubmit={handleRotate} autoComplete="off">
            <AntiAutofillHoneypot />
            <Input
              type="password"
              label="Passphrase corrente"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              leftIcon={<Icon name="lock_open" className="text-base" />}
              autoComplete="current-password"
              required
              hint="Serve a provare al server che sei tu: la passphrase non cambia."
            />
            {error ? (
              <div className="rounded-lg border border-error/60 bg-error-container/30 px-4 py-3 text-sm text-error">
                {error}
              </div>
            ) : null}
            <Button
              type="submit"
              size="lg"
              loading={working}
              rightIcon={!working ? <Icon name="arrow_forward" className="text-base" /> : null}
            >
              {working ? 'Genero il nuovo codice…' : 'Genera un nuovo codice'}
            </Button>
          </form>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={handleCheck} autoComplete="off">
            <AntiAutofillHoneypot />
            <Input
              label="Codice di recupero"
              placeholder="XXXX-XXXX-XXXX-…"
              value={candidateCode}
              onChange={(e) => {
                setCandidateCode(e.target.value);
                setCheckResult(null);
              }}
              leftIcon={<Icon name="password" className="text-base" />}
              autoComplete="off"
              spellCheck={false}
              required
              hint="Controllo locale: nulla viene modificato."
            />
            {checkResult === 'valid' ? (
              <div className="rounded-lg border border-cyan/60 bg-surface-container px-4 py-3 text-sm text-cyan">
                Questo codice è valido: sblocca la tua chiave privata.
              </div>
            ) : null}
            {checkResult === 'invalid' ? (
              <div className="rounded-lg border border-error/60 bg-error-container/30 px-4 py-3 text-sm text-error">
                Questo codice non sblocca il tuo vault. Genera un nuovo codice finché hai la
                sessione aperta.
              </div>
            ) : null}
            <Button type="submit" size="lg" loading={working}>
              {working ? 'Verifico…' : 'Verifica codice'}
            </Button>
          </form>
        )}

        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate('/', { replace: true })}
          disabled={working}
        >
          Torna alla home
        </Button>
      </Card>
    </div>
  );
}
